import { useState, useEffect, useMemo, useCallback } from 'react';
import { ATENCION_STORAGE_KEY } from '../constants';

const ESTADO_DEFAULT = {
  estadoAdministrativo: 'Pendiente de revisión',
  proximaAccion: 'Revisar consulta administrativa',
  observacion: '',
};

const LEGACY_STORAGE_KEYS = [
  'weconnect_atencion_local',
  'weconnect_atencion_local_data_v1',
];

const getConvId = (convOrId) => {
  if (!convOrId) return null;
  if (typeof convOrId === 'object') {
    return convOrId.id ?? convOrId.conversacion_id ?? convOrId.conversation_id ?? null;
  }
  return convOrId;
};

const getEstadoFromRaw = (raw = {}) => {
  return (
    raw.estadoAdministrativo ||
    raw.estado_atencion ||
    raw.estado_administrativo ||
    raw.estado_admin ||
    (raw.campo === 'estado_atencion' ? raw.valor : null) ||
    (raw.campo === 'estado_administrativo' ? raw.valor : null) ||
    null
  );
};

const getProximaFromRaw = (raw = {}) => {
  return (
    raw.proximaAccion ||
    raw.proxima_accion ||
    raw.siguiente_accion ||
    null
  );
};

const normalizarAtencion = (raw = {}) => {
  const estado = getEstadoFromRaw(raw);
  const proxima = getProximaFromRaw(raw);

  return {
    estadoAdministrativo: estado || ESTADO_DEFAULT.estadoAdministrativo,
    estado_atencion: estado || ESTADO_DEFAULT.estadoAdministrativo,
    estado_administrativo: estado || ESTADO_DEFAULT.estadoAdministrativo,
    proximaAccion: proxima || ESTADO_DEFAULT.proximaAccion,
    proxima_accion: proxima || ESTADO_DEFAULT.proximaAccion,
    observacion: raw.observacion ?? raw.nota ?? ESTADO_DEFAULT.observacion,
  };
};

const leerStorage = () => {
  const acumulado = {};

  try {
    for (const key of LEGACY_STORAGE_KEYS) {
      const raw = localStorage.getItem(key);
      if (!raw) continue;

      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object') {
        Object.assign(acumulado, parsed);
      }
    }

    const rawPrincipal = localStorage.getItem(ATENCION_STORAGE_KEY);
    if (rawPrincipal) {
      const parsedPrincipal = JSON.parse(rawPrincipal);
      if (parsedPrincipal && typeof parsedPrincipal === 'object') {
        Object.assign(acumulado, parsedPrincipal);
      }
    }
  } catch (e) {
    console.error('Error leyendo atención localStorage:', e);
  }

  return acumulado;
};

const escribirStorage = (data) => {
  try {
    localStorage.setItem(ATENCION_STORAGE_KEY, JSON.stringify(data || {}));
  } catch (e) {
    console.error('Error guardando atención localStorage:', e);
  }
};

export default function useAtencion({
  conversacionActiva,
  estadoOperativoBackend,
  guardarEstadoOperativo,
}) {
  const [atencionLocalData, setAtencionLocalData] = useState(() => leerStorage());

  useEffect(() => {
    escribirStorage(atencionLocalData);
  }, [atencionLocalData]);

  const actualizarAtencionLocal = useCallback((convId, patch = {}) => {
    const id = getConvId(convId);
    if (!id) return false;

    const key = String(id);
    const normalizado = normalizarAtencion(patch);

    setAtencionLocalData((prev) => {
      const anterior = prev[key] || {};

      const next = {
        ...prev,
        [key]: {
          ...anterior,
          ...normalizado,
          actualizadoLocalmente: true,
          updatedAt: new Date().toISOString(),
        },
      };

      escribirStorage(next);
      return next;
    });

    return true;
  }, []);

  useEffect(() => {
    const id = getConvId(conversacionActiva);
    if (!id || !estadoOperativoBackend) return;

    const key = String(id);
    const estadoBackend = getEstadoFromRaw(estadoOperativoBackend);
    const proximaBackend = getProximaFromRaw(estadoOperativoBackend);

    if (!estadoBackend && !proximaBackend && estadoOperativoBackend.observacion == null) return;

    setAtencionLocalData((prev) => {
      const actualRaw = prev[key] || {};
      const estadoLocal = getEstadoFromRaw(actualRaw);
      const backendNormalizado = normalizarAtencion(estadoOperativoBackend);

      let estadoFinal = backendNormalizado.estadoAdministrativo;

      if (
        estadoLocal &&
        estadoLocal !== ESTADO_DEFAULT.estadoAdministrativo &&
        estadoBackend === ESTADO_DEFAULT.estadoAdministrativo
      ) {
        estadoFinal = estadoLocal;
      }

      const nextForKey = {
        ...actualRaw,
        ...backendNormalizado,
        estadoAdministrativo: estadoFinal,
        estado_atencion: estadoFinal,
        estado_administrativo: estadoFinal,
        sincronizadoBackend: true,
        updatedAtBackend: new Date().toISOString(),
      };

      const next = {
        ...prev,
        [key]: nextForKey,
      };

      escribirStorage(next);
      return next;
    });
  }, [
    conversacionActiva?.id,
    estadoOperativoBackend?.estado_atencion,
    estadoOperativoBackend?.estado_administrativo,
    estadoOperativoBackend?.valor,
    estadoOperativoBackend?.proxima_accion,
    estadoOperativoBackend?.observacion,
  ]);

  const obtenerAtencionLocal = useCallback((conv) => {
    const id = getConvId(conv);
    if (!id) return { ...ESTADO_DEFAULT };

    const key = String(id);
    const rawLocal = atencionLocalData[key] || {};
    const local = normalizarAtencion(rawLocal);

    const esActiva =
      conversacionActiva?.id &&
      String(conversacionActiva.id) === key;

    if (!esActiva) {
      return local;
    }

    const estadoBackend = getEstadoFromRaw(estadoOperativoBackend || {});
    const backend = normalizarAtencion(estadoOperativoBackend || {});

    let estadoFinal = local.estadoAdministrativo;

    if (
      estadoBackend &&
      estadoBackend !== ESTADO_DEFAULT.estadoAdministrativo &&
      (!estadoFinal || estadoFinal === ESTADO_DEFAULT.estadoAdministrativo)
    ) {
      estadoFinal = estadoBackend;
    }

    return {
      estadoAdministrativo: estadoFinal || ESTADO_DEFAULT.estadoAdministrativo,
      estado_atencion: estadoFinal || ESTADO_DEFAULT.estadoAdministrativo,
      estado_administrativo: estadoFinal || ESTADO_DEFAULT.estadoAdministrativo,
      proximaAccion:
        local.proximaAccion ||
        backend.proximaAccion ||
        ESTADO_DEFAULT.proximaAccion,
      proxima_accion:
        local.proxima_accion ||
        backend.proxima_accion ||
        ESTADO_DEFAULT.proximaAccion,
      observacion:
        local.observacion ??
        backend.observacion ??
        ESTADO_DEFAULT.observacion,
    };
  }, [atencionLocalData, conversacionActiva?.id, estadoOperativoBackend]);

  const obtenerAtencion = useCallback((conv) => {
    return obtenerAtencionLocal(conv);
  }, [obtenerAtencionLocal]);

  const atencionActual = useMemo(() => {
    return obtenerAtencionLocal(conversacionActiva);
  }, [obtenerAtencionLocal, conversacionActiva]);

  const accionRapidaAtencion = async (accion) => {
    if (!conversacionActiva?.id) return false;

    const map = {
      factura: {
        estadoAdministrativo: 'Factura enviada',
        proximaAccion: 'Esperar confirmación del cliente',
      },
      pago: {
        estadoAdministrativo: 'Esperando pago',
        proximaAccion: 'Verificar acreditación',
      },
      resuelto: {
        estadoAdministrativo: 'Resuelto',
        proximaAccion: 'Sin acción pendiente',
      },
    };

    const next = map[accion];
    if (!next) return false;

    actualizarAtencionLocal(conversacionActiva.id, {
      ...next,
      observacion: atencionActual?.observacion || '',
    });

    if (guardarEstadoOperativo) {
      await guardarEstadoOperativo({
        area: 'atencion',
        campo: 'estado_atencion',
        valor: next.estadoAdministrativo,
        proxima_accion: next.proximaAccion,
        observacion: atencionActual?.observacion || null,
      });
    }

    return true;
  };

  return {
    atencionLocalData,
    atencionActual,
    actualizarAtencionLocal,
    obtenerAtencionLocal,
    obtenerAtencion,
    accionRapidaAtencion,
  };
}
